import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { createNewCenter, deleteCenter, editCenter, getCenter, getCenters } from "../../api/centerAPI";
import { CenterData, CenterDataCreate, ScheduleCreate } from "../../types/api/center";
import Loader from "../../components/Loader/Loader";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import Modal from "../../components/UI/Modal/Modal";
import Search from "../../components/Search/Search";
import {
    Button,
    Pagination,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@mui/material";
import { dayArr, MODAL_TYPE } from "../../constants/common";
import CustomSnackbar from "../../components/UI/CustomSnackbar";
import { useNotification } from "../../hooks/useNotification";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import { CenterForm } from "../../components/forms/CenterForm";
import { normalizeArray } from "../../utils/normalizeArray";

const CentersPage = () => {
    const isAdmin = localStorage.getItem("admin_role") === "true";
    const isCenterOwner = Number(localStorage.getItem("employee_center_id"));
    const isOwner = localStorage.getItem("employee_type") == "16";

    const [load, setLoad] = useState(false);
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
    });
    const [centers, setCenters] = useState<Array<CenterData>>();
    const [centerId, setCenterId] = useState(0);
    const [deleteCenterId, setDeleteCenterId] = useState(0);
    const [query, setQuery] = useState("");
    const { setNotification, setOpenSnackbar, notification, openSnackbar } = useNotification();
    const [showModal, setShowModal] = useState<boolean>(false);
    const [currentModalType, setCurrentModalType] = useState("edit");
    const closeModal = () => {
        setShowModal(false);
    };
    const checkedInitialState = {
        0: true,
        1: true,
        2: true,
        3: true,
        4: true,
        5: true,
        6: true,
    };
    const [checkedWeekend, setCheckedWeekend] = useState<any>(checkedInitialState);

    const createForm = useForm<CenterDataCreate>();
    const editForm = useForm<CenterData>();

    const setData = (id: any) => {
        localStorage.setItem("current_center_id", id);
    };

    const fetchCenters = (query?: string) => {
        setLoad(true);
        getCenters(
            ({ data, last_page, current_page }) => {
                setCenters(data);
                setPagination({ ...pagination, last_page: last_page, current_page: current_page });
                setLoad(false);
            },
            err => {
                setNotification({
                    severity: "error",
                    message: err.message || "",
                });
                setLoad(false);
            },
            query ? { page: pagination.current_page, q: query } : { page: pagination.current_page },
        );
    };

    const editResetFormData = (data: CenterData) => {
        editForm.reset({
            name: data?.name,
            phone: data?.phone,
            email: data?.email,
            chief_name: data?.chief_name,
            chief_work_position: data?.chief_work_position,
            status: data?.status,
            address: {
                city: data?.address?.city,
                street: data?.address?.street,
                building: data?.address?.building,
                index: data?.address?.index,
            },
            company: {
                name: data?.company?.name,
                inn: data?.company?.inn,
            },
            schedule: Object.keys(dayArr).reduce((acc, _cur, key) => {
                const index = Number(key);
                const scheduleDay = data?.schedule?.find(elem => elem.day === index + 1);
                setCheckedWeekend((prevState: any) => {
                    return {
                        ...prevState,
                        [key]: !scheduleDay?.day,
                    };
                });

                if (scheduleDay?.day) {
                    return [
                        ...acc,
                        {
                            day: scheduleDay?.day,
                            from: scheduleDay?.from,
                            to: scheduleDay?.to,
                        },
                    ];
                } else {
                    return acc;
                }
            }, [] as Array<ScheduleCreate>),
        });
    };

    const getCenterData = (centerId: number) => {
        setLoad(true);
        getCenter(
            centerId,
            ({ data }) => {
                setLoad(false);
                setShowModal(true);

                editResetFormData(data);
            },
            err => {
                setLoad(false);
                setNotification({
                    severity: "error",
                    message: err.message || "",
                });
            },
        );
    };

    const create: SubmitHandler<CenterDataCreate> = (data: any) => {
        const prepareData = { ...data, ...(data.schedule ? { schedule: normalizeArray(data.schedule) } : {}) };
        setLoad(true);
        createNewCenter(
            prepareData,
            () => {
                fetchCenters(query);
                setNotification({
                    severity: "success",
                    message: "Центр добавлен успешно",
                });
                setOpenSnackbar(true);
                setLoad(false);
                closeModal();
            },
            err => {
                setNotification({
                    severity: "error",
                    message: err.message || "",
                });
                setOpenSnackbar(true);
                setLoad(false);
                if (err?.errors) {
                    Object.keys(err.errors).forEach((fieldName: any) => {
                        createForm.setError(fieldName, {
                            type: "custom",
                            message: err.errors[fieldName][0],
                        });
                    });
                }
            },
        );
    };

    const edit: SubmitHandler<any> = (data: any) => {
        setLoad(true);
        editCenter(
            centerId,
            data,
            ({ data }) => {
                editResetFormData(data);
                fetchCenters(query);
                setNotification({
                    severity: "success",
                    message: "Центр изменен успешно",
                });
                setOpenSnackbar(true);
                setLoad(false);
                closeModal();
            },
            err => {
                setNotification({
                    severity: "error",
                    message: err.message || "",
                });
                setLoad(false);
                setOpenSnackbar(true);
                if (err?.errors) {
                    Object.keys(err.errors).forEach((fieldName: any) => {
                        editForm.setError(fieldName, {
                            type: "custom",
                            message: err.errors[fieldName][0],
                        });
                    });
                }
            },
        );
    };
    const del: SubmitHandler<any> = () => {
        setLoad(true);
        deleteCenter(
            deleteCenterId,
            () => {
                fetchCenters(query);
                setNotification({
                    severity: "success",
                    message: "Центр удален успешно",
                });
                setOpenSnackbar(true);
                setLoad(false);
            },
            err => {
                setNotification({
                    severity: "error",
                    message: err.message || "",
                });
                setOpenSnackbar(true);
                setLoad(false);
            },
        );
        closeModal();
    };

    const createContent = (
        <FormProvider {...createForm}>
            <CenterForm checkedWeekend={checkedWeekend} setCheckedWeekend={setCheckedWeekend} />
        </FormProvider>
    );
    const editContent = (
        <FormProvider {...editForm}>
            <CenterForm checkedWeekend={checkedWeekend} setCheckedWeekend={setCheckedWeekend} editForm />
        </FormProvider>
    );
    const deleteContent = <div>Вы уверены, что хотите удалить данные о центре?</div>;
    const modalType = {
        [MODAL_TYPE.create]: {
            title: "Создание центра",
            onSubmit: createForm.handleSubmit(create),
            content: createContent,
        },
        [MODAL_TYPE.edit]: {
            title: "Редактирование центра",
            onSubmit: editForm.handleSubmit(edit),
            content: editContent,
        },
        [MODAL_TYPE.delete]: {
            title: "Удаление центра",
            onSubmit: del,
            content: deleteContent,
        },
    };

    useEffect(() => {
        setLoad(true);
        fetchCenters(query);
    }, [pagination.current_page, query]);

    useEffect(() => {
        setCheckedWeekend(checkedInitialState);
        centerId && getCenterData(centerId);
    }, [centerId]);

    return (
        <>
            <div className={"container"}>
                <div className={"pageHeader"}>Центры</div>
                <Search
                    clearHandler={value => {
                        setQuery(value);
                    }}
                    searchHandler={value => {
                        setQuery(value);
                    }}
                />
                <TableContainer component={Paper} sx={{ marginBottom: "80px" }}>
                    <Table aria-label="centers table" sx={{ "& td": { border: "none" } }}>
                        <TableHead sx={{ "& th": { fontSize: "16px", fontWeight: 700 } }}>
                            <TableRow>
                                <TableCell>Наименование</TableCell>
                                <TableCell align="center">Руководитель</TableCell>
                                <TableCell align="center">Адрес</TableCell>
                                <TableCell align="center"></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {centers?.map(center => (
                                <TableRow key={center.id} sx={{ border: 0 }}>
                                    <TableCell
                                        sx={{
                                            wordWrap: "break-word",
                                            a: {
                                                color: "inherit",
                                                textDecoration: "none",
                                                display: "flex",
                                                alignItems: "center",
                                            },
                                        }}
                                        onClick={() => setData(center.id)}>
                                        <Link to={`${center.id}/data`}>
                                            {center.name} {center.id === isCenterOwner && <StarBorderIcon />}
                                        </Link>
                                    </TableCell>
                                    <TableCell align="center">{center.chief_name}</TableCell>
                                    <TableCell align="center">{center.address.street}</TableCell>
                                    <TableCell align="center">
                                        {(isAdmin || (isOwner && center.id === isCenterOwner)) && (
                                            <div className={"btnContainer"}>
                                                <Button
                                                    variant="outlined"
                                                    fullWidth
                                                    onClick={() => {
                                                        centerId === center.id && setShowModal(true);
                                                        setCenterId(center.id);
                                                        setCurrentModalType(MODAL_TYPE.edit);
                                                    }}>
                                                    Изменить
                                                </Button>
                                                <Button
                                                    fullWidth
                                                    variant="outlined"
                                                    onClick={() => {
                                                        setShowModal(true);
                                                        setDeleteCenterId(center.id);
                                                        setCurrentModalType(MODAL_TYPE.delete);
                                                    }}>
                                                    Удалить
                                                </Button>
                                            </div>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <div className={"posBR"}>
                    <div className={"paginationContainer"}>
                        <Pagination
                            onChange={(index, qwerty) => setPagination({ ...pagination, current_page: qwerty })}
                            page={pagination.current_page}
                            count={pagination.last_page}
                            shape="rounded"
                        />
                    </div>
                    {isAdmin && (
                        <Button
                            variant="outlined"
                            onClick={() => {
                                createForm.reset();
                                setCheckedWeekend(checkedInitialState);
                                setCurrentModalType(MODAL_TYPE.create);
                                setShowModal(true);
                            }}>
                            Добавить
                        </Button>
                    )}
                </div>
            </div>
            <Modal
                open={showModal}
                title={modalType[currentModalType]?.title}
                onClose={() => {
                    closeModal();
                }}
                onSubmit={modalType[currentModalType]?.onSubmit}>
                {modalType[currentModalType]?.content}
            </Modal>
            <CustomSnackbar openSnackbar={openSnackbar} setOpenSnackbar={setOpenSnackbar} notification={notification} />
            <Loader backDrop open={load} />
        </>
    );
};

export default CentersPage;
